import { useQuery } from '@tanstack/react-query';
import { api, ModelDefinition, ModelProvider, ParameterDefinition } from '../api';
import { queryKeys } from '../query-keys';

/**
 * Options for the useModelDefinition hook
 */
export interface UseModelDefinitionOptions {
  /** Entity ID to fetch by ID */
  entityId?: string;
  /** Provider to lookup by provider and model name */
  provider?: ModelProvider;
  /** Model name to lookup by provider and model name */
  modelName?: string;
  /** Auto-fetch on mount (default: true) */
  autoFetch?: boolean;
}

/**
 * Result returned by the useModelDefinition hook
 */
export interface UseModelDefinitionResult {
  /** The model definition (if loaded) */
  definition: ModelDefinition | null;
  /** The parameter schema from the definition */
  parameterSchema: ParameterDefinition[];
  /** Whether the model supports thinking mode */
  supportsThinking: boolean;
  /** Whether the model is a reasoning model */
  isReasoningModel: boolean;
  /** Maximum output tokens */
  maxOutputTokens: number;
  /** Loading state */
  loading: boolean;
  /** Error if fetch failed */
  error: Error | null;
  /** Manually refresh the definition */
  refresh: () => Promise<void>;
}

/**
 * React hook for fetching a Model Definition Entity by ID or provider/modelName lookup.
 *
 * Returns the full model definition including parameter schema, which can be used
 * to render data-driven parameter controls.
 *
 * @example
 * ```tsx
 * function ParameterEditor({ modelId }) {
 *   const { definition, parameterSchema, loading, error } = useModelDefinition({
 *     entityId: modelId,
 *   });
 *
 *   if (loading) return <Spinner />;
 *   if (error) return <Error message={error.message} />;
 *   if (!definition) return null;
 *
 *   return (
 *     <EntityParameterPanel
 *       parameterSchema={parameterSchema}
 *       values={values}
 *       onChange={setValues}
 *     />
 *   );
 * }
 * ```
 *
 * @example Lookup by provider and model name
 * ```tsx
 * const { definition } = useModelDefinition({
 *   provider: 'openai',
 *   modelName: 'gpt-5.2-2025-12-11',
 * });
 * ```
 */
export function useModelDefinition(
  options: UseModelDefinitionOptions = {},
): UseModelDefinitionResult {
  const { entityId, provider, modelName, autoFetch = true } = options;

  const hasEntityId = !!entityId;
  const hasLookup = !!provider && !!modelName;
  const hasIdentifiers = hasEntityId || hasLookup;

  const {
    data: definition,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: hasEntityId
      ? queryKeys.models.definition(entityId!)
      : hasLookup
        ? queryKeys.models.definitionByLookup(provider!, modelName!)
        : queryKeys.models.definition('__none__'),
    queryFn: () => {
      if (entityId) {
        return api.getModelDefinition(entityId);
      }
      return api.getModelDefinitionByLookup(provider!, modelName!);
    },
    enabled: autoFetch && hasIdentifiers,
  });

  return {
    definition: definition ?? null,
    parameterSchema: definition?.parameterSchema || [],
    supportsThinking: definition?.capabilities?.supportsThinking || false,
    isReasoningModel: definition?.capabilities?.isReasoningModel || false,
    maxOutputTokens: definition?.capabilities?.maxOutputTokens || 4096,
    loading: isLoading,
    error: error instanceof Error ? error : error ? new Error(String(error)) : null,
    refresh: async () => {
      await refetch();
    },
  };
}

/**
 * Creates default parameter values from a parameter schema
 */
export function createDefaultParameterValues(
  schema: ParameterDefinition[],
): Record<string, unknown> {
  const defaults: Record<string, unknown> = {};

  for (const param of schema) {
    // Get default from explicit defaultValue or range.default
    let defaultValue: unknown;

    if (param.defaultValue !== undefined) {
      defaultValue = param.defaultValue;
    } else if (param.type === 'number' && param.range) {
      defaultValue = param.range.default;
    } else if (param.type === 'enum' && param.options && param.options.length > 0) {
      defaultValue = param.options[0].value;
    } else if (param.type === 'boolean') {
      defaultValue = false;
    }

    if (defaultValue !== undefined) {
      defaults[param.name] = defaultValue;
    }
  }

  return defaults;
}

/**
 * Checks if a parameter should be visible based on its dependencies
 */
export function isParameterVisible(
  definition: ParameterDefinition,
  values: Record<string, unknown>,
): boolean {
  if (!definition.dependsOn) {
    return true;
  }

  const dependentValue = values[definition.dependsOn.parameter];
  return dependentValue === definition.dependsOn.value;
}
